import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { FormattedMessage, defineMessages } from 'react-intl';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { Text } from '@atlaskit/primitives/text';

import { Stack } from '@atlaskit/primitives/stack';

import {
	PlacementFrontend,
	type placementRenderProps,
} from '@post-office/browser--jsm-phantom/jira-issue-view-playbooks-panel';

import Link from '@atlaskit/link/link';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const messages = defineMessages({
	heading: {
		id: 'post-office.jsm-phantom.issue-view.playbook.spotlight-heading',
		defaultMessage: 'Resolve work items faster with playbooks',
		description: 'Heading for playbook spotlight.',
	},
	firstLine: {
		id: 'post-office.jsm-phantom.issue-view.playbook.spotlight-first-line',
		defaultMessage: `Follow step-by-step guidance and run automate actions to resolve work faster.`,
		description: 'First body line for playbook spotlight.',
	},
	secondLine: {
		id: 'post-office.jsm-phantom.issue-view.playbook.spotlight-second-line',
		defaultMessage: `No playbook yet? Share your runbooks with project admin to turn them into actionable playbooks.`,
		description: 'Second body line for playbook spotlight.',
	},
	learnMoreLink: {
		id: 'post-office.jsm-phantom.issue-view.playbook.spotlight-learn-more',
		defaultMessage: `Learn more about playbooks`,
		description: 'Learn more link for playbook spotlight.',
	},
	closeButton: {
		id: 'post-office.jsm-phantom.issue-view.playbook.spotlight-close-button',
		defaultMessage: 'Close',
		description: 'close button to dismiss spotlight.',
	},
});

// Frontend
const render = PlacementFrontend.createFrontendStage({
	type: 'render',

	validate: noValidation<placementRenderProps>(),

	Component({ dismiss }: placementRenderProps) {
		const { onClosed } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const handleDismiss = useCallback(() => {
			void onClosed({ interactionIdentifier: 'dismiss' });
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'playbookIssueViewSpotlightDismissClick',
			});
			dismiss();
		}, [dismiss, onClosed, fireAnalyticsEvent]);

		const onLinkClick = useCallback(() => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'playbookIssueViewSpotlightLinkClick',
			});
		}, [fireAnalyticsEvent]);

		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* dummy div, the spotlight positioning will be handled by CSS */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={handleDismiss} placement="bottom-end" isVisible={true}>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage {...messages.heading} />
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={handleDismiss} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Stack space="space.100">
								<Text>
									<FormattedMessage {...messages.firstLine} />
								</Text>
								<Text>
									<FormattedMessage {...messages.secondLine} />{' '}
									<Link href={YOUTUBE_LINK} target="_blank" onClick={onLinkClick}>
										<FormattedMessage {...messages.learnMoreLink} />
									</Link>
								</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleDismiss}>
									<FormattedMessage {...messages.closeButton} />
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
});

const YOUTUBE_LINK = 'https://www.youtube.com/watch?v=RPm7e5C0N-E';

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
